import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Users, ChevronDown, MessageSquare, X, Download, Share2 } from 'lucide-react';
import axios from 'axios';
import { buildApiUrl } from '../../../../services/apiConfig';
import FilterPanel from './FilterPanel';
import ChatInterface from './ChatInterface';
import ClientList from './ClientList';
import { PPT_BACKGROUND, PPT_LOGO } from '../../../../constants/pptConstants';

const ClientPersonaExplorerTab = ({ navigateClientInsights }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatQuery, setChatQuery] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [userId, setUserId] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [clientToNavigate, setClientToNavigate] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    personas: [],
    industries: [],
    locations: [],
    assetTiers: [],
    interests: []
  });
  const [availableFilters, setAvailableFilters] = useState({
    personas: [],
    industries: [],
    locations: [],
    assetTiers: [],
    interests: []
  });
  const [expandedFilters, setExpandedFilters] = useState({
    personas: true,
    industries: true,
    locations: true,
    assetTiers: true,
    interests: true
  });
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [searchSource, setSearchSource] = useState('filter');

  useEffect(() => {
    const storedUserId = sessionStorage.getItem('user_id');
    setUserId(storedUserId || 'U1757535255');
  }, []);

  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    fetchFilters();
    fetchSavedSearches();
    fetchAllClients();
  }, []);

  useEffect(() => {
    if (Object.values(availableFilters).some(arr => arr.length > 0) && searchSource === 'filter') {
      fetchClients();
    }
  }, [selectedFilters, searchQuery, searchSource]);

  const fetchAllClients = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(buildApiUrl('AUTH', '/api/client-search'), {
        searchQuery,
        selectedFilters
      });
      if (response.data.success) {
        setClients(response.data.clients);
        setFilteredClients(response.data.clients);
      } else {
        console.error('Failed to fetch clients:', response.data.error);
        setClients([]);
        setFilteredClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setFilteredClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilters = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(buildApiUrl('SQL', '/api/client-personas'));
      if (response.data.success) {
        setAvailableFilters(response.data.filters);
      } else {
        console.error('Failed to fetch filters:', response.data.error);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(buildApiUrl('SQL', '/api/client-search'), {
        searchQuery,
        selectedFilters
      });
      if (response.data.success) {
        setClients(response.data.clients);
        setFilteredClients(response.data.clients);
      } else {
        console.error('Failed to fetch clients:', response.data.error);
        setClients([]);
        setFilteredClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setFilteredClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSavedSearches = async () => {
    try {
      const response = await axios.get(buildApiUrl('SQL', '/api/saved-searches'));
      if (response.data.success) {
        setSavedSearches(response.data.searches);
      } else {
        console.error('Failed to fetch saved searches:', response.data.error);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    }
  };

  const toggleFilter = (category, id) => {
    setSearchSource('filter');
    setSelectedFilters(prev => {
      const updated = { ...prev };
      if (updated[category].includes(id)) {
        updated[category] = updated[category].filter(item => item !== id);
      } else {
        updated[category] = [...updated[category], id];
      }
      return updated;
    });
  };

  const toggleExpandFilter = (category) => {
    setExpandedFilters(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const clearFilters = () => {
    setSearchSource('filter');
    setSelectedFilters({
      personas: [],
      industries: [],
      locations: [],
      assetTiers: [],
      interests: []
    });
    setSearchQuery('');
  };

  const toggleClientExpand = (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      const selectedClient = filteredClients.find(client => client.id === clientId);
      if (selectedClient) {
        setClientToNavigate(selectedClient);
      }
    }
  };

  const handleOpenInClientInsights = (client) => {
    const formattedClient = {
      ...client,
      id: client.id,
      name: client.name,
      img: client.image,
      phone: client.phone,
      email: client.email,
      address: client.address,
      total_aum: client.total_aum,
      ytd_return: client.ytd_return,
      time_horizon_category: client.time_horizon_category,
      last_contact_date: client.last_contact_date,
      net_worth_category: client.net_worth_category,
      risk_tolerance: client.risk_tolerance,
      investment_goal: client.investment_goal,
      next_review_date: client.next_review_date,
      client_since: client.client_since
    };
    navigateClientInsights(formattedClient);
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatQuery.trim()) return;

    setChatHistory(prev => [...prev, { role: 'user', content: chatQuery }]);
    const currentQuery = chatQuery + 'from Observatory.dbo.person without mentioning the database.';
    setChatQuery('');
    setAutoScroll(true);
    setIsTyping(true);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('question', currentQuery);
      formData.append('advisor_id', userId);
      formData.append('use_case_id', 'tst2');

      const response = await fetch(buildApiUrl('SQL_AGENT', '/sql-ask'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      setIsTyping(false);

      if (data && data.response && data.response.answer) {
        const answer = data.response.answer;
        let displayedAnswer = "";

        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: displayedAnswer,
          isTyping: true
        }]);

        // Default to empty chat results; will be overwritten if clients are matched
        setSearchSource('chat');
        setFilteredClients([]);

        const typingSpeed = 15;

        for (let i = 0; i <= answer.length; i++) {
          displayedAnswer = answer.substring(0, i);
          setChatHistory(prev => {
            const newHistory = [...prev];
            newHistory[newHistory.length - 1] = {
              role: 'assistant',
              content: displayedAnswer,
              isTyping: i < answer.length
            };
            return newHistory;
          });

          await new Promise(resolve => setTimeout(resolve, typingSpeed));
        }

        if (data.response.result) {
          try {
            const resultData = JSON.parse(data.response.result);
            if (Array.isArray(resultData) && resultData.length > 0 && typeof resultData[0] === 'object') {
              // Show as table in chat, hide typed answer
              setChatHistory(prev => {
                const newHistory = [...prev];
                newHistory[newHistory.length - 1] = {
                  ...newHistory[newHistory.length - 1],
                  content: '',
                  tableData: resultData
                };
                return newHistory;
              });

              // Try to match result rows against loaded clients by ID first
              const ID_FIELDS = ['id', 'customer_id', 'cust_id', 'person_id', 'client_id'];
              const resultIds = resultData
                .flatMap(row => ID_FIELDS.map(f => row[f]).filter(Boolean))
                .map(String);

              let matched = resultIds.length > 0
                ? clients.filter(c => resultIds.includes(String(c.id)))
                : [];

              // Fall back to name matching against loaded clients
              if (matched.length === 0) {
                const resultNames = resultData.map(row =>
                  `${row.first_name || ''} ${row.last_name || ''}`.trim() ||
                  row.name || row.full_name || row.client_name || ''
                ).filter(Boolean).map(n => n.toLowerCase());

                if (resultNames.length > 0) {
                  matched = clients.filter(c =>
                    resultNames.some(n => c.name?.toLowerCase().includes(n) || n.includes(c.name?.toLowerCase() || ''))
                  );
                }
              }

              if (matched.length > 0) {
                setFilteredClients(matched);
                setSearchSource('chat');
              } else {
                // Last resort: search API by name
                const clientNames = resultData.map(row =>
                  `${row.first_name || ''} ${row.last_name || ''}`.trim() ||
                  row.name || row.full_name || row.client_name || ''
                ).filter(Boolean);

                if (clientNames.length > 0) {
                  setSearchSource('chat');
                  setIsLoading(true);
                  const allClients = [];
                  const batchSize = 5;

                  for (let i = 0; i < clientNames.length; i += batchSize) {
                    const batch = clientNames.slice(i, i + batchSize);
                    const batchResults = await Promise.all(
                      batch.map(name =>
                        axios.post(buildApiUrl('SQL', '/api/client-search'), {
                          searchQuery: name,
                          selectedFilters: { personas: [], industries: [], locations: [], assetTiers: [], interests: [] }
                        })
                      )
                    );
                    batchResults.forEach(res => {
                      if (res.data.success && res.data.clients.length > 0) {
                        allClients.push(...res.data.clients);
                      }
                    });
                  }

                  const uniqueClients = allClients.length > 0
                    ? [...new Map(allClients.map(c => [c.id, c])).values()]
                    : formatClientResults(resultData);
                  setFilteredClients(uniqueClients);
                } else {
                  setFilteredClients([]);
                  setSearchSource('chat');
                }
              }
            }
          } catch (error) {
            console.error('Error parsing search results:', error);
          }
        }
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error processing chat query:', error);
      setIsTyping(false);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatClientResults = (resultData) => {
    return resultData.map((client, index) => {
      const clientId = client.id || `chat-client-${index}`;
      const name = `${client.first_name || ''} ${client.last_name || ''}`.trim() ||
        client.name ||
        'Unknown Client';

      return {
        id: clientId,
        name: name,
        image: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${(index % 70) + 1}.jpg`,
        company: client.current_company_name || client.company || 'Unknown',
        role: client.current_company_title || client.role || 'Professional',
        relationshipStatus: client.relationshipStatus || 'Prospect',
        location: client.home_state || client.location || 'Unknown',
        industry: client.industry_type || client.industry || 'Unknown',
        assetTier: client.assetTier || 'Unknown',
        personas: client.personas || [client.industry_type || 'Individual Investor'],
        interests: client.interests || [],
        details: {
          background: client.background || "Information not available",
          financialProfile: client.financialProfile || `${client.name || 'Client'} has an estimated net worth in the ${client.assetTier || 'Unknown'} range.`,
        },
        advisorFit: client.advisorFit || Math.floor(Math.random() * 30) + 70
      };
    });
  };

  const scrollToBottom = () => {
    setAutoScroll(true);
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const handleActionClick = (action) => {
    if (action.type === 'apply_filters') {
      setSelectedFilters(action.filters);
      setShowChat(false);
    }
  };

  const applySavedSearch = (search) => {
    setSearchSource('filter');
    setSelectedFilters(search.filters);
  };

  const processInterests = (interestsArray) => {
    if (!interestsArray) return [];

    return interestsArray
      .filter(interest =>
        interest !== true &&
        interest !== false &&
        interest !== null &&
        interest !== undefined
      )
      .map(interest => String(interest).trim())
      .filter(interest => interest !== '');
  };

  const exportToPPT = async () => {
    if (filteredClients.length === 0) {
      alert("No clients to export. Please search for clients first.");
      return;
    }

    try {
      // Show loading state
      setIsLoading(true);

      // Prepare slides data for the export API
      const slidesData = [
        // Title slide
        {
          type: "text",
          title: "Client Persona Explorer Results",
          data: `Export generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}\n\n${filteredClients.length} clients found${searchSource === 'chat' ? ' from chat query' : ''}`
        },

        // Summary slide
        {
          type: "text",
          title: "Client Summary",
          data: generateSummaryText()
        },

        // Client details slides (create a slide for each client)
        ...filteredClients.map(client => ({
          type: "text",
          title: `Client Profile: ${client.name}`,
          data: generateClientProfileText(client)
        })),

        // Distribution chart by industry
        {
          type: "pie",
          title: "Clients by Industry",
          chart_title: "Distribution by Industry",
          data: generateIndustryDistribution(),
          chart_config: {
            series_names: ["Clients"]
          }
        },

        // Distribution chart by location
        {
          type: "column",
          title: "Clients by Location",
          chart_title: "Distribution by Location",
          data: generateLocationDistribution(),
          chart_config: {
            series_names: ["Clients"]
          }
        },

        // Client table
        {
          type: "table",
          title: "Client List",
          data: filteredClients.map(client => ({
            Name: client.name,
            Company: client.company,
            Role: client.role,
            Location: client.location,
            Industry: client.industry,
            "Asset Tier": client.assetTier,
            Personas: Array.isArray(client.personas) ? client.personas.join(", ") : client.personas
          }))
        }
      ];

      // Call the export API
      const response = await fetch(buildApiUrl('EXPORT', '/export-native-ppt-with-charts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slides: slidesData,
          has_dark_background: true,
          logo: PPT_LOGO,
          background: PPT_BACKGROUND,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API responded with status: ${response.status}. Details: ${errorText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'client_persona_explorer_results.pptx';
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting to PowerPoint:', error);
      alert('Failed to export to PowerPoint. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateSummaryText = () => {
    const industriesCounts = {};
    filteredClients.forEach(client => {
      const industry = client.industry || 'Unknown';
      industriesCounts[industry] = (industriesCounts[industry] || 0) + 1;
    });

    const locationCounts = {};
    filteredClients.forEach(client => {
      const location = client.location || 'Unknown';
      locationCounts[location] = (locationCounts[location] || 0) + 1;
    });

    const assetTierCounts = {};
    filteredClients.forEach(client => {
      const assetTier = client.assetTier || 'Unknown';
      assetTierCounts[assetTier] = (assetTierCounts[assetTier] || 0) + 1;
    });

    let summary = `This report contains information on ${filteredClients.length} clients.\n\n`;

    if (Object.values(selectedFilters).some(arr => arr.length > 0)) {
      summary += "Applied Filters:\n";

      Object.entries(selectedFilters).forEach(([category, selected]) => {
        if (selected.length > 0) {
          const filterNames = selected.map(id =>
            availableFilters[category].find(o => o.id === id)?.name || id
          ).join(", ");

          summary += `- ${category.charAt(0).toUpperCase() + category.slice(1)}: ${filterNames}\n`;
        }
      });

      summary += "\n";
    }

    summary += "Industry Breakdown:\n";
    Object.entries(industriesCounts)
      .sort((a, b) => b[1] - a[1])
      .forEach(([industry, count]) => {
        const percentage = ((count / filteredClients.length) * 100).toFixed(1);
        summary += `- ${industry}: ${count} clients (${percentage}%)\n`;
      });

    summary += "\n";

    summary += "Location Breakdown:\n";
    Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .forEach(([location, count]) => {
        const percentage = ((count / filteredClients.length) * 100).toFixed(1);
        summary += `- ${location}: ${count} clients (${percentage}%)\n`;
      });

    summary += "\n";

    summary += "Asset Tier Breakdown:\n";
    Object.entries(assetTierCounts)
      .sort((a, b) => b[1] - a[1])
      .forEach(([tier, count]) => {
        const percentage = ((count / filteredClients.length) * 100).toFixed(1);
        summary += `- ${tier}: ${count} clients (${percentage}%)\n`;
      });

    return summary;
  };

  const generateClientProfileText = (client) => {


    const displayInterests = processInterests(client.interests);

    let profileText = `Name: ${client.name}\n`;
    profileText += `Company: ${client.company || 'N/A'}\n`;
    profileText += `Role: ${client.role || 'N/A'}\n`;
    profileText += `Location: ${client.location || 'N/A'}\n`;
    profileText += `Industry: ${client.industry || 'N/A'}\n`;
    profileText += `Asset Tier: ${client.assetTier || 'N/A'}\n\n`;

    if (client.personas && client.personas.length > 0) {
      profileText += `Personas: ${client.personas.join(', ')}\n\n`;
    }

    if (displayInterests.length > 0) {
      profileText += `Interests: ${displayInterests.join(', ')}\n\n`;
    } else {
      profileText += "Interests: No specific interests recorded\n\n";
    }

    profileText += "Background:\n";
    profileText += client.details?.background || "No background information available.\n\n";

    profileText += "\nFinancial Profile:\n";
    profileText += client.details?.financialProfile || "No financial profile available.\n\n";

    return profileText;
  };

  const generateIndustryDistribution = () => {
    const industriesCounts = {};

    filteredClients.forEach(client => {
      const industry = client.industry || 'Unknown';
      industriesCounts[industry] = (industriesCounts[industry] || 0) + 1;
    });

    return Object.entries(industriesCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); 
  };

  const generateLocationDistribution = () => {
    const locationCounts = {};

    filteredClients.forEach(client => {
      const location = client.location || 'Unknown';
      locationCounts[location] = (locationCounts[location] || 0) + 1;
    });

    return Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); 
  };


  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Client Persona Explorer</h2>
      <p className="text-gray-600 mb-6">
        Search and filter clients by persona, industry, location, and interests to identify targeted client segments and opportunities.
      </p>
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left sidebar - filters */}
        <div className="w-full lg:w-1/4">
          <FilterPanel
            availableFilters={availableFilters}
            selectedFilters={selectedFilters}
            expandedFilters={expandedFilters}
            toggleFilter={toggleFilter}
            toggleExpandFilter={toggleExpandFilter}
            clearFilters={clearFilters}
          />
        </div>

        <div className="w-full lg:w-3/4">
          {/* Search and chat toggle */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchSource('filter');
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search clients by name, company, industry..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <button
              onClick={() => setShowChat(!showChat)}
              className={`flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium ${showChat
                ? 'bg-blue-50 text-primaryDark border-blue-300'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
            >
              <MessageSquare size={18} className="mr-2" />
              Chat Query
            </button>
          </div>

          {/* Applied filters */}
          {Object.values(selectedFilters).some(arr => arr.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(selectedFilters).map(([category, selected]) =>
                selected.map(id => {
                  const option = availableFilters[category].find(o => o.id === id);
                  if (!option) return null;

                  return (
                    <div
                      key={`${category}-${id}`}
                      className="bg-blue-50 text-primaryDark text-xs rounded-full px-3 py-1 flex items-center"
                    >
                      <span>{option.name}</span>
                      <button
                        onClick={() => toggleFilter(category, id)}
                        className="ml-1.5 text-primary hover:text-primaryDark"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })
              )}

              <button
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Chat interface */}
          {showChat && (
            <ChatInterface
              chatHistory={chatHistory}
              chatQuery={chatQuery}
              setChatQuery={setChatQuery}
              handleChatSubmit={handleChatSubmit}
              isTyping={isTyping}
              autoScroll={autoScroll}
              scrollToBottom={scrollToBottom}
              handleActionClick={handleActionClick}
            />
          )}

          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              {filteredClients.length} {filteredClients.length === 1 ? 'client' : 'clients'} found
              {searchSource === 'chat' && (
                <span className="ml-2 text-primaryDark">
                  (from chat query)
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={exportToPPT}
                className="text-sm text-gray-600 flex items-center hover:text-gray-900"
                disabled={isLoading || filteredClients.length === 0}
              >
                <Download size={16} className="mr-1" />
                {isLoading ? "Exporting..." : "Export"}
              </button>
              <button className="text-sm text-gray-600 flex items-center hover:text-gray-900">
                <Share2 size={16} className="mr-1" />
                Share
              </button>
            </div>
          </div>

          {/* Client list */}
          <ClientList
            filteredClients={filteredClients}
            expandedClient={expandedClient}
            toggleClientExpand={toggleClientExpand}
            handleOpenInClientInsights={handleOpenInClientInsights}
            processInterests={processInterests}
            clearFilters={clearFilters}
            searchSource={searchSource}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientPersonaExplorerTab;